import { repeatedFieldAddress } from "../groupAnswers";
import { useRef, useState } from "react";
import { Button } from "@sfc/ui";
import { PlusIcon } from "lucide-react";
import { newId } from "../ids";
import { isBlankEntry, entryValue } from "../types";
import {
  repeatFieldsOf,
  entryLabelOf,
  maxEntriesOf,
  minEntriesOf,
  fieldQuestion,
  visibleEntryFields,
  visibleRepeatedAnswer,
} from "../repeatable";
import { questionAnswerIssues } from "../response";
import { renderFor, type QuestionRenderProps } from "./render";

export function RepeatableGroupRender({
  question,
  value,
  onChange,
  labelId,
  invalid,
  shuffleSeed,
  renderHelp,
}: QuestionRenderProps) {
  const [placeholderId, setPlaceholderId] = useState(() => newId("entry"));
  const savedEntries = value?.kind === "entries" ? value.entries : [];
  // Once a starter is saved, reserve a fresh identity for the next empty state.
  // This also handles a teammate removing the last row.
  if (savedEntries.some((entry) => entry.id === placeholderId))
    setPlaceholderId(newId("entry"));
  // Display a starter row without writing a blank answer or counting it as complete.
  const entries = savedEntries.length
    ? savedEntries
    : [{ id: placeholderId, values: {} }];
  const effectiveValue = visibleRepeatedAnswer(question, value);
  const answeredIds = new Set(
    effectiveValue?.kind === "entries"
      ? effectiveValue.entries
          .filter((entry) => !isBlankEntry(entry))
          .map((entry) => entry.id)
      : [],
  );
  const answeredCount = answeredIds.size;
  const fields = repeatFieldsOf(question);
  const entryLabel = entryLabelOf(question);
  const max = maxEntriesOf(question);
  const min = Math.max(question.required ? 1 : 0, minEntriesOf(question));
  const focusEntry = useRef<string | null>(null);
  const addButton = useRef<HTMLButtonElement>(null);
  return (
    <div
      className="flex flex-col gap-5"
      role="group"
      aria-labelledby={labelId}
      aria-invalid={invalid || undefined}
    >
      <p className="body-sm text-muted-foreground">
        {question.required
          ? `Complete at least ${min} ${min === 1 ? "entry" : "entries"}.`
          : "Optional — you can leave this entire group blank."}
        {!question.required && min > 1
          ? ` If you answer, complete at least ${min} entries.`
          : ""}
      </p>
      {entries.map((entry, index) => (
        <fieldset
          key={entry.id}
          className={`min-w-0 border-t pt-4 ${invalid ? "border-destructive" : "border-border"}`}
          ref={(element) => {
            if (element && focusEntry.current === entry.id) {
              element
                .querySelector<HTMLInputElement>("input, textarea")
                ?.focus();
              focusEntry.current = null;
            }
          }}
        >
          <legend className="label-md px-2">
            {entryLabel} {index + 1}
          </legend>
          <div className="grid gap-4 sm:grid-cols-2">
            {visibleEntryFields(question, entry).map((field) => {
              const Render = renderFor(field.type);
              const childQuestion = {
                ...fieldQuestion(field),
                id: repeatedFieldAddress(question.id, entry.id, field.id),
              };
              const value = entryValue(entry, field.id);
              const fieldLabelId = `${labelId}-${entry.id}-${field.id}`;
              const issues =
                invalid &&
                (answeredIds.has(entry.id) ||
                  (question.required && answeredCount === 0))
                  ? questionAnswerIssues(childQuestion, value)
                  : [];
              return (
                <div
                  key={field.id}
                  className={`flex min-w-0 flex-col gap-2 ${field.type === "paragraph" ? "sm:col-span-2" : ""}`}
                >
                  <span id={fieldLabelId} className="label-md">
                    {field.label}
                    {field.required ? (
                      <span aria-hidden="true" className="text-destructive">
                        {" "}
                        *
                      </span>
                    ) : null}
                  </span>
                  {field.help
                    ? (renderHelp?.(field.help) ?? (
                        <p className="body-sm text-muted-foreground whitespace-pre-wrap">
                          {field.help}
                        </p>
                      ))
                    : null}
                  <Render
                    onBeforeUpload={() => {
                      if (!savedEntries.some((saved) => saved.id === entry.id))
                        onChange({ kind: "entries", entries });
                    }}
                    question={childQuestion}
                    value={value}
                    labelId={fieldLabelId}
                    shuffleSeed={shuffleSeed}
                    invalid={issues.length > 0}
                    onChange={(next) => {
                      onChange({
                        kind: "entries",
                        entries: entries.map((current) =>
                          current.id === entry.id
                            ? {
                                ...current,
                                values: {
                                  ...current.values,
                                  [field.id]:
                                    next?.kind === "text"
                                      ? next.value
                                      : next?.kind !== "entries"
                                        ? (next ?? "")
                                        : "",
                                },
                              }
                            : current,
                        ),
                      });
                    }}
                  />
                  {issues.map((issue) => (
                    <p key={issue} className="body-sm text-destructive">
                      {issue}
                    </p>
                  ))}
                </div>
              );
            })}
          </div>
          {savedEntries.length > 0 && (
            <div className="mt-2 flex justify-end">
              <Button
                type="button"
                variant="nav"
                className="text-muted-foreground pointer-coarse:min-h-11"
                aria-label={`Remove ${entryLabel.toLowerCase()} ${index + 1}`}
                onClick={() => {
                  const remaining = entries.filter(
                    (current) => current.id !== entry.id,
                  );
                  onChange(
                    remaining.length
                      ? { kind: "entries", entries: remaining }
                      : undefined,
                  );
                  addButton.current?.focus();
                }}
              >
                Remove {entryLabel.toLowerCase()}
              </Button>
            </div>
          )}
        </fieldset>
      ))}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          ref={addButton}
          type="button"
          variant="outline"
          className="pointer-coarse:min-h-11"
          disabled={entries.length >= max || fields.length === 0}
          onClick={() => {
            const id = newId("entry");
            focusEntry.current = id;
            onChange({
              kind: "entries",
              entries: [...entries, { id, values: {} }],
            });
          }}
        >
          <PlusIcon />
          Add {entryLabel.toLowerCase()}
        </Button>
        <span className="body-sm text-muted-foreground" aria-live="polite">
          {answeredCount}{" "}
          {question.config?.maxEntries === undefined
            ? "entries started"
            : `of ${max} entries started`}
        </span>
      </div>
    </div>
  );
}
